'use client';

import { type InputHTMLAttributes, type Ref } from 'react';
import { type FieldError } from 'react-hook-form';

import { Icon } from '@/components/icons/Icon';

import { cn } from '@/_helpers/cn';

export interface ICheckbox extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  classNames?: string;
  className?: string;
  defaultChecked?: boolean;
  error?: FieldError;
  disabled?: boolean;
  checked?: boolean;
  readOnly?: boolean;
  ref?: Ref<HTMLInputElement>;
}

export const Checkbox = ({
  label,
  classNames,
  className,
  defaultChecked = false,
  checked,
  error,
  readOnly = false,
  disabled = false,
  ref,
  ...rest
}: ICheckbox) => {
  const labelClass = cn('relative flex cursor-pointer items-center', {
    'cursor-not-allowed opacity-50': disabled,
  });

  const inputClass = cn(
    'before:content[""] peer relative h-5 w-5 cursor-pointer appearance-none rounded-sm border transition-all',
    'before:absolute before:left-2/4 before:top-2/4 before:block before:-translate-x-2/4 before:-translate-y-2/4 before:rounded-full before:bg-gray-600 before:opacity-0 before:transition-opacity',
    'checked:border-primary checked:bg-primary checked:before:bg-primary',
    {
      'border-red-500': error,
      'border-gray-600': !error,
      'opacity-50 cursor-not-allowed bg-gray-200 border-gray-200 checked:bg-gray-200 checked:border-gray-200': disabled,
    },
    classNames,
    className,
  );

  return (
    <div className="flex items-center justify-start">
      <label className={labelClass}>
        <div className="relative flex items-center justify-center">
          <input
            type="checkbox"
            ref={ref}
            defaultChecked={checked === undefined ? defaultChecked : undefined}
            checked={checked}
            disabled={disabled}
            readOnly={readOnly}
            className={inputClass}
            {...rest}
          />
          <span className="pointer-events-none absolute left-2/4 top-2/4 -translate-x-2/4 -translate-y-2/4 opacity-0 transition-opacity peer-checked:opacity-100">
            <Icon icon="checkMark" iconClass={`${disabled ? 'text-gray-400' : 'text-white'} h-3.5 w-3.5`} />
          </span>
        </div>
        <div>
          {label && (
            <span className={`ml-2 mt-px flex select-none text-sm ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
              {label}
            </span>
          )}
          {error && <span className="ml-2 mt-px flex select-none text-xs text-accent-red">{error?.message}</span>}
        </div>
      </label>
    </div>
  );
};
